/**
 * HFDS — Headzflex Design System
 * Component: HF-001 Foundation Package
 * Version: 1.0.0
 *
 * Shared utilities only.
 * Product, gallery, cart and variant logic must live in separate modules.
 */

(() => {
  "use strict";

  if (window.HFDS) {
    return;
  }

  const BREAKPOINTS = Object.freeze({
    xs: 0,
    sm: 480,
    md: 768,
    lg: 1024,
    xl: 1440
  });

  const EVENT_PREFIX = "hfds:";

  /**
   * Returns a single matching element.
   *
   * @param {string} selector
   * @param {ParentNode} [context=document]
   * @returns {Element|null}
   */
  function query(selector, context = document) {
    if (!selector || !context) {
      return null;
    }

    return context.querySelector(selector);
  }

  /**
   * Returns an array of matching elements.
   *
   * @param {string} selector
   * @param {ParentNode} [context=document]
   * @returns {Element[]}
   */
  function queryAll(selector, context = document) {
    if (!selector || !context) {
      return [];
    }

    return Array.from(context.querySelectorAll(selector));
  }

  /**
   * Executes a function after the DOM is ready.
   *
   * @param {Function} callback
   */
  function ready(callback) {
    if (typeof callback !== "function") {
      return;
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback, {
        once: true
      });
      return;
    }

    callback();
  }

  /**
   * Debounces repeated calls.
   *
   * @param {Function} callback
   * @param {number} [delay=150]
   * @returns {Function}
   */
  function debounce(callback, delay = 150) {
    let timeoutId;

    return function debounced(...args) {
      window.clearTimeout(timeoutId);

      timeoutId = window.setTimeout(() => {
        callback.apply(this, args);
      }, delay);
    };
  }

  /**
   * Throttles repeated calls with requestAnimationFrame.
   *
   * @param {Function} callback
   * @returns {Function}
   */
  function throttleFrame(callback) {
    let scheduled = false;
    let lastArgs = [];

    return function throttled(...args) {
      lastArgs = args;

      if (scheduled) {
        return;
      }

      scheduled = true;

      window.requestAnimationFrame(() => {
        callback.apply(this, lastArgs);
        scheduled = false;
      });
    };
  }

  /**
   * Creates and dispatches a namespaced HFDS event.
   *
   * @param {string} eventName
   * @param {Object} [detail={}]
   * @param {EventTarget} [target=document]
   * @returns {boolean}
   */
  function emit(eventName, detail = {}, target = document) {
    if (!eventName || !target) {
      return false;
    }

    const event = new CustomEvent(`${EVENT_PREFIX}${eventName}`, {
      bubbles: true,
      cancelable: true,
      detail
    });

    return target.dispatchEvent(event);
  }

  /**
   * Subscribes to a namespaced HFDS event.
   *
   * @param {string} eventName
   * @param {EventListener} handler
   * @param {EventTarget} [target=document]
   * @param {AddEventListenerOptions|boolean} [options]
   * @returns {Function} Unsubscribe function
   */
  function on(
    eventName,
    handler,
    target = document,
    options = undefined
  ) {
    if (!eventName || typeof handler !== "function" || !target) {
      return () => {};
    }

    const fullEventName = `${EVENT_PREFIX}${eventName}`;

    target.addEventListener(fullEventName, handler, options);

    return () => {
      target.removeEventListener(fullEventName, handler, options);
    };
  }

  /**
   * Returns the active HFDS breakpoint.
   *
   * @returns {"xs"|"sm"|"md"|"lg"|"xl"}
   */
  function getBreakpoint() {
    const width = window.innerWidth;

    if (width >= BREAKPOINTS.xl) {
      return "xl";
    }

    if (width >= BREAKPOINTS.lg) {
      return "lg";
    }

    if (width >= BREAKPOINTS.md) {
      return "md";
    }

    if (width >= BREAKPOINTS.sm) {
      return "sm";
    }

    return "xs";
  }

  /**
   * Checks whether a viewport is at or above a breakpoint.
   *
   * @param {"xs"|"sm"|"md"|"lg"|"xl"} breakpoint
   * @returns {boolean}
   */
  function isMinBreakpoint(breakpoint) {
    const value = BREAKPOINTS[breakpoint];

    if (typeof value !== "number") {
      return false;
    }

    return window.innerWidth >= value;
  }

  /**
   * Checks whether reduced motion is preferred.
   *
   * @returns {boolean}
   */
  function prefersReducedMotion() {
    return window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
  }

  /**
   * Safely parses JSON.
   *
   * @param {string} value
   * @param {*} [fallback=null]
   * @returns {*}
   */
  function parseJSON(value, fallback = null) {
    if (typeof value !== "string" || value.trim() === "") {
      return fallback;
    }

    try {
      return JSON.parse(value);
    } catch (error) {
      console.warn("[HFDS] Invalid JSON:", error);
      return fallback;
    }
  }

  /**
   * Retrieves JSON from a script element.
   *
   * @param {string|Element} source
   * @param {*} [fallback=null]
   * @returns {*}
   */
  function getJSONScript(source, fallback = null) {
    const element =
      typeof source === "string"
        ? query(source)
        : source;

    if (!element) {
      return fallback;
    }

    return parseJSON(element.textContent, fallback);
  }

  /**
   * Generates a simple unique identifier.
   *
   * @param {string} [prefix="hf"]
   * @returns {string}
   */
  function createId(prefix = "hf") {
    if (
      window.crypto &&
      typeof window.crypto.randomUUID === "function"
    ) {
      return `${prefix}-${window.crypto.randomUUID()}`;
    }

    return `${prefix}-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}`;
  }

  /**
   * Locks document scrolling while preserving scrollbar width.
   */
  function lockScroll() {
    const scrollbarWidth =
      window.innerWidth -
      document.documentElement.clientWidth;

    document.documentElement.style.setProperty(
      "--hf-scrollbar-width",
      `${scrollbarWidth}px`
    );

    document.documentElement.classList.add("hf-scroll-lock");
    document.body.classList.add("hf-scroll-lock");

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight =
        "var(--hf-scrollbar-width)";
    }
  }

  /**
   * Restores document scrolling.
   */
  function unlockScroll() {
    document.documentElement.classList.remove("hf-scroll-lock");
    document.body.classList.remove("hf-scroll-lock");
    document.body.style.removeProperty("padding-right");
    document.documentElement.style.removeProperty(
      "--hf-scrollbar-width"
    );
  }

  /**
   * Creates an IntersectionObserver.
   *
   * @param {Function} callback
   * @param {IntersectionObserverInit} [options]
   * @returns {IntersectionObserver|null}
   */
  function createObserver(callback, options = {}) {
    if (
      typeof callback !== "function" ||
      !("IntersectionObserver" in window)
    ) {
      return null;
    }

    return new IntersectionObserver(callback, {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
      ...options
    });
  }

  /**
   * Announces a message to screen readers.
   *
   * @param {string} message
   * @param {"polite"|"assertive"} [priority="polite"]
   */
  function announce(message, priority = "polite") {
    if (!message) {
      return;
    }

    let region = query(
      `[data-hf-live-region="${priority}"]`
    );

    if (!region) {
      region = document.createElement("div");
      region.className = "hf-visually-hidden";
      region.dataset.hfLiveRegion = priority;
      region.setAttribute("aria-live", priority);
      region.setAttribute("aria-atomic", "true");
      document.body.appendChild(region);
    }

    region.textContent = "";

    window.requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  /**
   * Determines whether an element is currently visible.
   *
   * @param {Element|null} element
   * @returns {boolean}
   */
  function isVisible(element) {
    if (!element) {
      return false;
    }

    const styles = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return (
      styles.display !== "none" &&
      styles.visibility !== "hidden" &&
      Number(styles.opacity) !== 0 &&
      rect.width > 0 &&
      rect.height > 0
    );
  }

  /**
   * Gets focusable descendants.
   *
   * @param {Element} container
   * @returns {HTMLElement[]}
   */
  function getFocusableElements(container) {
    if (!container) {
      return [];
    }

    const selector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "summary",
      '[tabindex]:not([tabindex="-1"])'
    ].join(",");

    return queryAll(selector, container).filter(isVisible);
  }

  /**
   * Traps keyboard focus inside a container.
   *
   * @param {Element} container
   * @returns {Function} Cleanup function
   */
  function trapFocus(container) {
    if (!container) {
      return () => {};
    }

    const previousActiveElement = document.activeElement;

    function handleKeydown(event) {
      if (event.key !== "Tab") {
        return;
      }

      const focusable = getFocusableElements(container);

      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();
        last.focus();
        return;
      }

      if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener("keydown", handleKeydown);

    const focusable = getFocusableElements(container);

    if (focusable.length > 0) {
      focusable[0].focus();
    } else if (container instanceof HTMLElement) {
      container.setAttribute("tabindex", "-1");
      container.focus();
    }

    return () => {
      container.removeEventListener("keydown", handleKeydown);

      if (
        previousActiveElement instanceof HTMLElement &&
        document.contains(previousActiveElement)
      ) {
        previousActiveElement.focus();
      }
    };
  }

  /**
   * Formats money using Shopify's active currency when available.
   *
   * @param {number} cents
   * @param {string} [currency]
   * @param {string} [locale]
   * @returns {string}
   */
  function formatMoney(
    cents,
    currency = window.Shopify?.currency?.active || "USD",
    locale = document.documentElement.lang || "en-US"
  ) {
    const numericCents = Number(cents);

    if (!Number.isFinite(numericCents)) {
      return "";
    }

    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency
      }).format(numericCents / 100);
    } catch (error) {
      console.warn("[HFDS] Money formatting failed:", error);
      return `$${(numericCents / 100).toFixed(2)}`;
    }
  }

  /**
   * Initializes global accessibility helpers.
   */
  function initializeAccessibility() {
    document.addEventListener("keydown", (event) => {
      if (event.key === "Tab") {
        document.documentElement.classList.add(
          "hf-keyboard-navigation"
        );
      }
    });

    document.addEventListener("pointerdown", () => {
      document.documentElement.classList.remove(
        "hf-keyboard-navigation"
      );
    });
  }

  /**
   * Emits a breakpoint change event.
   */
  function initializeBreakpointObserver() {
    let activeBreakpoint = getBreakpoint();

    const handleResize = debounce(() => {
      const nextBreakpoint = getBreakpoint();

      if (nextBreakpoint === activeBreakpoint) {
        return;
      }

      const previousBreakpoint = activeBreakpoint;
      activeBreakpoint = nextBreakpoint;

      emit("breakpoint:change", {
        previous: previousBreakpoint,
        current: nextBreakpoint,
        width: window.innerWidth
      });
    }, 120);

    window.addEventListener("resize", handleResize, {
      passive: true
    });
  }

  const HFDS = Object.freeze({
    version: "1.0.0",
    breakpoints: BREAKPOINTS,
    query,
    queryAll,
    ready,
    debounce,
    throttleFrame,
    emit,
    on,
    getBreakpoint,
    isMinBreakpoint,
    prefersReducedMotion,
    parseJSON,
    getJSONScript,
    createId,
    lockScroll,
    unlockScroll,
    createObserver,
    announce,
    isVisible,
    getFocusableElements,
    trapFocus,
    formatMoney
  });

  window.HFDS = HFDS;

  ready(() => {
    initializeAccessibility();
    initializeBreakpointObserver();

    document.documentElement.classList.add("hfds-ready");

    emit("ready", {
      version: HFDS.version,
      breakpoint: getBreakpoint()
    });
  });
})();